import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ModerationActivity } from '../components/moderation/ModerationActivity';
import { PolicyForm } from '../components/moderation/PolicyForm';
import { useAuth } from '../contexts/AuthContext';
import { MODE_TEXT, apiError, liveWarning, type PolicyFields } from '../lib/moderationView';
import { useBotRoute } from '../lib/useBotRoute';
import { hubService, type ModerationPolicyView } from '../services/hub.service';

const TABS = [
  { key: 'settings', label: 'Settings' },
  { key: 'activity', label: 'Activity' },
] as const;

const SOURCE: Record<string, string> = {
  bot: 'This bot has its own settings.',
  platform: 'Using the platform default (set by the platform team).',
  default: 'Using the built-in default (watch only).',
};

/** Bot workspace → Moderation (spec 2026-10-06-comment-moderation, M2): policy + what moderation did. */
export function BotModeration() {
  const { system, botId } = useBotRoute();
  const { getToken } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'activity' ? 'activity' : 'settings';
  const [view, setView] = useState<ModerationPolicyView | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setView(await hubService.getModerationPolicy(system, botId, getToken()));
  }, [system, botId, getToken]);

  useEffect(() => {
    setView(null);
    setMsg(null);
    load().catch((e) => setMsg(apiError(e)));
  }, [load]);

  const save = async (fields: PolicyFields) => {
    try {
      setView(await hubService.setModerationPolicy(system, botId, fields, getToken()));
      setMsg('Saved.');
    } catch (e) {
      setMsg(apiError(e));
      throw e;
    }
  };

  const reset = async () => {
    try {
      setView(await hubService.resetModerationPolicy(system, botId, getToken()));
      setMsg('Back to the platform default.');
    } catch (e) {
      setMsg(apiError(e));
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Comment moderation</h1>
        <div role="tablist" className="inline-flex overflow-hidden rounded border border-gray-300 text-sm">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setParams(t.key === 'activity' ? { tab: 'activity' } : {}, { replace: true })}
              className={`px-3 py-1 ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-gray-600">
        Hides abuse and spam on this bot's pages; complaints are flagged for you, never hidden.
        {view && (
          <>
            {' '}
            Current mode: <b>{MODE_TEXT[view.effective.mode]}</b>.
          </>
        )}
      </p>
      {view && liveWarning(view.effective, view.run) && (
        <p className="rounded bg-red-50 p-2 text-sm text-red-800">{liveWarning(view.effective, view.run)}</p>
      )}
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      {tab === 'settings' ? (
        view && (
          <>
            <PolicyForm
              key={`${system}:${botId}`}
              value={view.effective}
              run={view.run}
              sourceNote={SOURCE[view.effective.source]}
              onSave={save}
              onReset={view.own ? reset : undefined}
            />
            {view.history.length > 0 && (
              <div className="text-xs text-gray-500">
                <p className="font-medium">History</p>
                <ul>
                  {view.history.map((h, i) => (
                    <li key={i}>
                      {new Date(h.createdAt).toLocaleString()} — {h.changedBy ?? 'unknown'}:{' '}
                      {h.after ? MODE_TEXT[h.after.mode] : 'reset to default'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )
      ) : (
        <ModerationActivity key={`${system}:${botId}`} system={system} botId={botId} />
      )}
    </div>
  );
}
